import '../styles.scss';

import { faCheck } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import React, { useMemo } from 'react';

import { CalendarInteractive } from '../../../../../components/calendar-interactive/calendar-interactive';
import { ChartTypeEnum } from '../../../../../model/chart_type.enum';
import { RangeEnum } from '../../../../../model/range.enum';
import {
  AreaChartIcon,
  BarChartIcon,
  CandleChartIcon,
  ColumnChartIcon,
  HollowCandlesChartIcon,
  LineChartIcon,
  LineWithMarkersChartIcon,
} from '../../../../../theme/customIcons';
import InlineOptions from '../../../inline-options';
import Divider from '../../../divider';

interface MarketHistoryRangeControlsProps {
  currentChartType?: ChartTypeEnum;
  chartTypeCallback?: (type: ChartTypeEnum) => void;
  initDate: Date;
  handleSetRange?: (range?: any) => void;
  handleApplyCalendar?: (startDate: string | Date, endDate: string | Date) => void;
  isCalendar?: boolean;
  isSelectChartType?: boolean;
  disabled?: boolean;
  range: RangeEnum;
  setRange?: (range: RangeEnum) => void;
}

export const MarketHistoryRangeControls = (props: MarketHistoryRangeControlsProps) => {
  const {
    currentChartType,
    chartTypeCallback,
    handleSetRange = () => {},
    handleApplyCalendar = () => {},
    isCalendar = true,
    isSelectChartType = true,
    disabled = false,
    range,
  } = props;

  const rangeOptions = useMemo(() => {
    const base = [
      { value: RangeEnum.D1 as string, label: '1D' },
      { value: RangeEnum.D5 as string, label: '5D' },
      { value: RangeEnum.M1 as string, label: '1M' },
      { value: RangeEnum.M3 as string, label: '3M' },
      { value: RangeEnum.M6 as string, label: '6M' },
      { value: RangeEnum.Y1 as string, label: '1Y' },
      { value: RangeEnum.ALL as string, label: 'ALL' },
    ];
    return base;
  }, []);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleChartTypeClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCharTypeClose = () => {
    setAnchorEl(null);
  };

  const [selectedType, setselectedType] = React.useState(currentChartType);

  const changeChartType = (event: React.MouseEvent<HTMLElement>, type: ChartTypeEnum) => {
    setselectedType(type);
    setAnchorEl(null);
    if (chartTypeCallback) {
      chartTypeCallback(type);
    }
  };

  const getIcon = (type: ChartTypeEnum) => {
    switch (type) {
      case ChartTypeEnum.CandleStick:
        return <CandleChartIcon className="chart-type-selection-button" />;
      case ChartTypeEnum.HollowCandles:
        return <HollowCandlesChartIcon className="chart-type-selection-button" />;
      case ChartTypeEnum.Bars:
        return <BarChartIcon className="chart-type-selection-button" />;
      case ChartTypeEnum.Area:
        return <AreaChartIcon className="chart-type-selection-button" />;
      case ChartTypeEnum.Columns:
        return <ColumnChartIcon className="chart-type-selection-button" />;
      case ChartTypeEnum.Line:
        return <LineChartIcon className="chart-type-selection-button" />;
      case ChartTypeEnum.LineWMarkers:
        return <LineWithMarkersChartIcon className="chart-type-selection-button" />;
    }
  };

  const handleOnClickRange = (newRange: RangeEnum) => {
    if (disabled || range === newRange) return;
    handleSetRange(newRange);
  };

  return (
    <Box className="chart-controls chart-controls-market-data">
      <Box className="chart-timing">
        <InlineOptions
          options={rangeOptions}
          selectedOption={range as unknown as string}
          onOptionChange={(val) => handleOnClickRange(val as unknown as RangeEnum)}
          classname="range-inline-options"
        />
        {isCalendar && (
          <CalendarInteractive
            isDateField={false}
            isSelectPeriod={false}
            onApply={handleApplyCalendar}
            disabled={disabled}
          />
        )}
        <Divider className="chart-controls-divider" />
      </Box>

      {isSelectChartType && (
        <Box className="chart-tools">
          <Button
            size="small"
            onClick={handleChartTypeClick}
            aria-controls={open ? 'chart-menu' : undefined}
            aria-haspopup="true"
            className="range-btn"
            aria-expanded={open ? 'true' : undefined}
          >
            {getIcon(selectedType as ChartTypeEnum)}
          </Button>

          <Menu
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            anchorEl={anchorEl}
            open={open}
            onClose={handleCharTypeClose}
            classes={{ paper: 'market-history-menu' }}
          >
            <MenuItem
              selected={selectedType === ChartTypeEnum.Bars}
              onClick={(event) => changeChartType(event, ChartTypeEnum.Bars)}
            >
              <ListItemIcon>
                <BarChartIcon className="menu-chart-icon" />
              </ListItemIcon>
              <ListItemText disableTypography>Bars</ListItemText>
              <ListItemIcon className="selected-check-icon">
                {selectedType === ChartTypeEnum.Bars && <FontAwesomeIcon icon={faCheck} />}
              </ListItemIcon>
            </MenuItem>
            <MenuItem
              selected={selectedType === ChartTypeEnum.CandleStick}
              onClick={(event) => changeChartType(event, ChartTypeEnum.CandleStick)}
            >
              <ListItemIcon>
                <CandleChartIcon className="menu-chart-icon" />
              </ListItemIcon>
              <ListItemText disableTypography>Candle</ListItemText>
              <ListItemIcon className="selected-check-icon">
                {selectedType === ChartTypeEnum.CandleStick && <FontAwesomeIcon icon={faCheck} />}
              </ListItemIcon>
            </MenuItem>
            <MenuItem
              selected={selectedType === ChartTypeEnum.HollowCandles}
              onClick={(event) => changeChartType(event, ChartTypeEnum.HollowCandles)}
            >
              <ListItemIcon>
                <HollowCandlesChartIcon className="menu-chart-icon" />
              </ListItemIcon>
              <ListItemText disableTypography>Hollow candles</ListItemText>
              <ListItemIcon className="selected-check-icon">
                {selectedType === ChartTypeEnum.HollowCandles && <FontAwesomeIcon icon={faCheck} />}
              </ListItemIcon>
            </MenuItem>

            <MenuItem
              selected={selectedType === ChartTypeEnum.Line}
              onClick={(event) => changeChartType(event, ChartTypeEnum.Line)}
            >
              <ListItemIcon>
                <LineChartIcon className="menu-chart-icon" />
              </ListItemIcon>
              <ListItemText disableTypography>Line</ListItemText>
            </MenuItem>
            <MenuItem
              selected={selectedType === ChartTypeEnum.LineWMarkers}
              onClick={(event) => changeChartType(event, ChartTypeEnum.LineWMarkers)}
            >
              <ListItemIcon>
                <LineWithMarkersChartIcon className="menu-chart-icon" />
              </ListItemIcon>
              <ListItemText disableTypography>Line with markers</ListItemText>
            </MenuItem>
            <MenuItem
              selected={selectedType === ChartTypeEnum.Area}
              onClick={(event) => changeChartType(event, ChartTypeEnum.Area)}
            >
              <ListItemIcon>
                <AreaChartIcon className="menu-chart-icon" />
              </ListItemIcon>
              <ListItemText disableTypography>Area</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      )}
    </Box>
  );
};
